<template>
    <ul
        :class="[
            $style['menu-list'],
            mobile ? $style['mobile-menu-list'] : $style['desktop-menu-list'],
            showAsColumn ? $style['column-list'] : '',
            dropdown ? $style['dropdown'] : '',
            onePerColumnLayout ? $style['one-per-column'] : '',
        ]"
        :data-level="level"
        ref="el"
        :style="styleObj"
    >
        <Item
            :level="level"
            v-if="showParentItemLink"
            :mobile="mobile"
            :dropdown-item="true"
            :menu-item="parentItem"
            disable-items
            first
            :last="parentLast && menuItems.length == 0 && !limitItems"
        ></Item>
        <template v-for="{ menuItem, i, n } in childItemsAndIndexes">
            <li
                v-if="n > 0 && onePerColumnLayout"
                :class="$style['vertical-line']"
            ></li>
            <FadeAndSlide>
                <Item
                    :level="level"
                    v-if="limit === 0 || n < limit"
                    :mobile="mobile"
                    :dropdown-item="dropdown"
                    :column-item="showAsColumn"
                    :show-sub-items-in-column="onePerColumnLayout"
                    :expanded="isExpanded(menuItem, focusStore.currentFocus)"
                    @expand="expandMenuItem(menuItem)"
                    @collapse="collapseMenuItem(menuItem)"
                    :menu-item="menuItem"
                    :key="i"
                    :first="i === 0 && !showParentItemLink"
                    :last="
                        parentLast && i === menuItems.length - 1 && !limitItems
                    "
                ></Item>
            </FadeAndSlide>
        </template>
        <Item
            v-if="limitItems"
            :level="level"
            pseudo
            :mobile="mobile"
            :dropdown-item="dropdown"
            :column-item="showAsColumn"
            :name="showAll ? labels.topSite.showLess : labels.topSite.showMore"
            :key="'showMore'"
            :bold="true"
            :smaller="mobile"
            :last="parentLast"
            @click="showAll = !showAll"
        ></Item>
    </ul>
</template>

<style module lang="scss">
@import "common.css.src/layers.scss";

.menu-list {
    margin: 0;
    padding: 0;
    flex-direction: column;
    list-style: none;
}
.desktop-menu-list {
    display: flex;
    flex-direction: row;
    align-items: center;

    &.column-list {
        flex-direction: column;
        align-items: flex-start;
    }
}
.vertical-line {
    display: block;
    place-self: stretch;
    height: auto;
    width: 1px;
    background-color: #0c0c0c;
    flex: 0;
    min-width: 1px;
}
.dropdown {
    margin-top: 10px;
    padding-left: 10px;
    &.desktop-menu-list {
        // display: block;
        // column-count: 3;
        display: grid;
        column-count: 3;
        grid-template-columns: repeat(3, minmax(180px, 1fr));
        grid-template-rows: repeat(8, 1fr);
        grid-auto-flow: column;
        grid-gap: 0 20px;
        position: absolute;
        left: 0px;
        max-width: 980px;
        width: 980px;
        margin-top: 0px;
        background: white;
        z-index: $zIndexMenus;
        margin-top: 13px;
        align-items: start;
        box-shadow: 0 5px 10px #00000052;
        padding: 30px 30px;

        &.one-per-column {
            display: flex;
            flex-direction: row;
        }
    }
}
</style>

<script setup lang="ts">
import { type PropType, computed, onMounted, ref, watch } from "vue";
import Item from "./item.vue";
import {
    iterateAllItemsFlattened,
    type PatchedMenuItem,
} from "./menu-item";
import FadeAndSlide from "../../shared/FadeAndSlide.vue";
import labels from "../../shared/labels";
import { type CSSProperties } from "vue/types/jsx";
import { type FocusElement, useFocus } from "../../../../state/focus";
import { ensureUniqueKey } from "../../../../shared/unique-identifier-key";
const props = defineProps({
    menuItems: {
        type: Array as PropType<PatchedMenuItem[]>,
        required: true,
    },
    parentItem: Object as PropType<PatchedMenuItem>,
    mobile: Boolean,
    dropdown: Boolean,
    parentLast: Boolean,
    showAsColumn: Boolean,
    level: {
        type: Number,
        required: true,
    },
    onePerColumnLayout: Boolean,
});

const focusStore = useFocus();

const showParentItemLink = false;
// const showParentItemLink = computed(
//     () =>
//         !props.showAsColumn &&
//         !props.onePerColumnLayout &&
//         props.dropdown &&
//         hasLink(props.parentItem)
// );

function getMenuUniqueKeyFromFocus(
    currentFocus: (typeof focusStore)["currentFocus"]
) {
    if (currentFocus == null) return undefined;
    if (currentFocus.key.indexOf("menu-item_") !== 0) return undefined;

    return currentFocus.key.substring("menu-item_".length);
}

function isExpanded(
    menuItem: PatchedMenuItem,
    currentFocus: string | (typeof focusStore)["currentFocus"]
): boolean {
    if (typeof currentFocus != "string") {
        if (currentFocus == null) return false;

        currentFocus = getMenuUniqueKeyFromFocus(currentFocus);

        if (currentFocus == null) return false;
    }

    const key = ensureUniqueKey(menuItem);

    if (currentFocus == key) {
        return true;
    }

    if (menuItem.items == null || menuItem.items.length === 0) return false;

    return menuItem.items.some((x) => isExpanded(x, currentFocus));
}

function getFriendlyName(menuItem: PatchedMenuItem): string {
    if (menuItem.parent == null) {
        return menuItem.name;
    }
    return getFriendlyName(menuItem.parent) + " > " + menuItem.name;
}

function findParentMenuItem(
    currentFocus: FocusElement
): FocusElement | undefined {
    const key = getMenuUniqueKeyFromFocus(currentFocus);

    for (const item of iterateAllItemsFlattened(props.menuItems[0])) {
        if (ensureUniqueKey(item) === key) {
            if (item.parent == null) break;
            return createFocusElement(item.parent);
        }
    }
    return undefined;
}

function createFocusElement(menuItem: PatchedMenuItem): FocusElement {
    return {
        parent: findParentMenuItem,
        friendlyName: getFriendlyName(menuItem),
        key: "menu-item_" + ensureUniqueKey(menuItem),
    };
}

function expandMenuItem(menuItem: PatchedMenuItem) {
    focusStore.set(createFocusElement(menuItem));
}

function collapseMenuItem(menuItem: PatchedMenuItem) {
    const previousFocus = focusStore.currentFocus;
    const uniqueKeyBefore = getMenuUniqueKeyFromFocus(previousFocus);

    if (uniqueKeyBefore == ensureUniqueKey(menuItem)) {
        focusStore.blurCurrent();
    } else {
        focusStore.set(createFocusElement(menuItem));
        focusStore.blurCurrent();
    }
}

const showAll = ref(false);

const limitCountDefault = computed(() => (props.mobile ? 5 : 15));

const limitItems = computed(() => {
    return (
        ((props.mobile && props.dropdown) ||
            (!props.mobile && props.showAsColumn)) &&
        props.menuItems.length > limitCountDefault.value
    );
});

const limit = computed(() => {
    if (!limitItems.value) {
        return 0;
    }
    return showAll.value ? 0 : limitCountDefault.value;
});

const el = ref<HTMLUListElement>();

const manualRows = computed(() => {
    if (!props.dropdown || props.mobile) return false;

    const cnt = props.menuItems.length;
    if (cnt < 9) return Math.ceil(cnt / 2);
    return Math.ceil(cnt / 3);
});

const styleObj = ref<CSSProperties>({});

function updateStyleValue(rows: false | number) {
    //console.log("setting rows", rows, el.value);
    if (rows !== false) {
        const value = `repeat(${rows}, 1fr)`;
        if (el.value != null) el.value.style.gridTemplateRows = value;
        styleObj.value["grid-template-rows"] = value;
    } else {
        if (el.value != null) el.value.style.gridTemplateRows = ``;
        styleObj.value["grid-template-rows"] = undefined;
    }
}

watch(
    () => manualRows.value,
    (rows) => {
        updateStyleValue(rows);
    },
    {
        immediate: true,
    }
);

const childItemsAndIndexes = computed(() => {
    return props.menuItems
        .map((menuItem, i) => ({
            menuItem,
            i,
        }))
        .filter(
            (x) =>
                props.dropdown == false ||
                props.onePerColumnLayout == false ||
                !(
                    x.menuItem.specialAttributes != null &&
                    "dontShowAsColumn" in x.menuItem.specialAttributes &&
                    x.menuItem.specialAttributes.dontShowAsColumn === "true"
                )
        )
        .flatMap((x) => {
            if (props.mobile && x.menuItem.transparentOnMobile === true) {
                return (
                    x.menuItem.items?.map((y, i) => ({ i: i, menuItem: y })) ??
                    []
                );
            }
            return [x];
        })
        .map((x, i) => ({ ...x, n: i }));
});

onMounted(() => {
    updateStyleValue(manualRows.value);
});
</script>
